import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, UserX } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable, DateCell } from '@components/common/table';
import ErrorState from '@components/common/ErrorState';
import { conflictService } from '@services';
import type { Conflict } from '@app-types/conflict';
import type { TableColumn } from '@app-types';
import { formatMediumDate } from '@utils/dateUtils';
import styles from './MissingContractRoleWorklistPage.module.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface MissingRoleAssignment {
  contact_id: number;
  contact_name: string;
  field: 'CO' | 'CM';
}

interface WorklistRow {
  conflict: Conflict;
  assignment: MissingRoleAssignment;
}

const columns: TableColumn[] = [
  { key: 'contact', label: 'Contact', defaultVisible: true },
  { key: 'contract', label: 'Contract', defaultVisible: true },
  { key: 'field', label: 'Field', defaultVisible: true },
  { key: 'detected', label: 'Detected', defaultVisible: true },
  { key: 'action', label: '', defaultVisible: true, alwaysVisible: true }
];

const getAssignments = (conflict: Conflict): MissingRoleAssignment[] => {
  const raw = conflict.context.missing_role_assignments;
  return Array.isArray(raw) ? (raw as MissingRoleAssignment[]) : [];
};

const MissingContractRoleWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['conflicts-worklist', 'MISSING_CONTRACT_ROLE', currentPage, pageSize],
    queryFn: () =>
      conflictService.getWorklist({
        conflictTypeCode: 'MISSING_CONTRACT_ROLE',
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      })
  });

  const rows: WorklistRow[] = (data?.conflicts ?? []).flatMap((conflict) => {
    const assignments = getAssignments(conflict);
    if (assignments.length === 0) {
      return [{ conflict, assignment: { contact_id: 0, contact_name: 'Unknown', field: 'CO' } }];
    }
    return assignments.map((assignment) => ({ conflict, assignment }));
  });
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderCell = (column: TableColumn, row: WorklistRow) => {
    switch (column.key) {
      case 'contact':
        return row.assignment.contact_name;
      case 'contract':
        return (
          <div className={styles.contractCell}>
            <div>{row.conflict.entity_info?.contract_number ?? String(row.conflict.context.contract_number ?? '-')}</div>
            <div className={styles.contractName}>{row.conflict.entity_info?.contract_name ?? ''}</div>
          </div>
        );
      case 'field':
        return <Badge variant="default">{row.assignment.field === 'CO' ? 'Contract Owner' : 'Contract Manager'}</Badge>;
      case 'detected':
        return <DateCell date={row.conflict.detected_at} formatDate={(d) => formatMediumDate(d, 'Unknown')} />;
      case 'action':
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/users?search=${encodeURIComponent(row.assignment.contact_name)}`);
            }}
            title="Open Users to assign Contract Role"
          >
            <ExternalLink size={16} />
          </Button>
        );
      default:
        return '-';
    }
  };

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Failed to load missing Contract Role worklist." />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Missing Contract Role">
      <ListPageHeader icon={<UserX size={24} />} title="Missing Contract Role" count={total} />

      <RZTable
        data={rows}
        columns={columns}
        getRowKey={(row) => `${row.conflict.id}-${row.assignment.contact_id}-${row.assignment.field}`}
        renderCell={renderCell}
        pagination={{
          currentPage,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          itemName: 'assignments',
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }}
        state={{
          isLoading,
          isFetching,
          isEmpty: rows.length === 0,
          emptyTitle: 'No missing Contract Role assignments',
          emptyDescription: 'Every assigned Contract Owner and Contract Manager currently holds the Contract Role.',
          loadingMessage: 'Loading missing Contract Role worklist...'
        }}
      />
    </PageLayout>
  );
};

export default MissingContractRoleWorklistPage;
